/* eslint-disable max-lines */
import {
  RemoveMessage,
  AddReactionMessage,
  RemovedReactionMessage,
  ReplacedReactionMessage,
  SocketData,
  ReadReactionMessage,
  AttachmentVisibilityContactModified,
  AttachmentVisibilityMessageModified,
  RequestForCommunicationStatusChangedComet,
  SocketContent,
  BaseRemoveMessage,
  LivenessComet,
  ChatDataUpdate,
} from 'actions/socket/socketReceiveDataAction'
import { MessengerTextMessageComet } from 'api/comet/comet.types'
import { MessengerItemTypes } from 'components/page/Chat/Messenger/Messenger.constants'
import {
  createAttachmentVisibilityContactModifiedChannel,
  createAttachmentVisibilityMessageModifiedChannel,
  createLastUserChannel,
  createLivenessSessionResultChannel,
  createMessageAddReactionChannel,
  createMessageEditedChannel,
  createMessageReadedChannel,
  createMessageReadReactionChannel,
  createMessageRemoveReactionChannel,
  createMessageReplaceReactionChannel,
  createMessengerAutodeleteChannel,
  createMessengerIgnoredChannel,
  createMessengerMessageChannel,
  createMessengerRequestForCommunicationStatusChangedChannel,
  createLastUserOutChannel,
  createRemovedIncomingMessageChannel,
  createRemovedMessageChannel,
  createSendingPhotoAbility,
  createSupportTickerClosedChannel,
  createTypingMessageUserChannel,
  createVipStatusChannel,
  createMessengerMessageOutChannel,
} from 'functions/comet'
import { filterCometMessages } from 'reducers/functions'
import {
  ActionComet,
  ChatMessengerState,
  MessageEditedCometContent,
  MessageFromCometContent,
  MessageIgnoredFromCometContent,
  MessageTypingFromCometContent,
} from 'reducers/messenger/ChatMessengerState'
import { addIgnoredMessage } from 'reducers/messenger/function/addIgnoredMessage'
import { addIncomingMessage } from 'reducers/messenger/function/addIncomingMessage'
import { addTypingSignal } from 'reducers/messenger/function/addTypingSignal'
import { markMessagesAsReaded } from 'reducers/messenger/function/markMessagesAsReaded'
import { updateChatDataFromOutgoing } from 'reducers/messenger/function/updateChatDataFromOutgoing'
import { updateEditedMessage } from 'reducers/messenger/function/updateEditedMessage'

import { addOutgoingMessage } from './addOutgoingMessage'
import { removeMessageFromChat } from './removeMessage'
import { updateAttachmentVisibilityContactModified } from './updateAttachmentVisibilityContactModified'
import { updateAttachmentVisibilityMessageModified } from './updateAttachmentVisibilityMessageModified'
import { updateChatData } from './updateChatData'
import {
  updateMessagesWithAddedReaction,
  updateMessagesWithReadReaction,
  updateMessagesWithRemovedReaction,
  updateMessagesWithReplacedReaction,
} from '../messenger.functions'

export const updateMessengerFromWebSocket = (
  userId: number,
  chatState: ChatMessengerState,
  socketResult: SocketData[],
  chatSelectedProfileId: number
): ChatMessengerState => {
  const updateChat = {
    ...chatState,
    forceRefetch: true,
    scrollToBottom: true,
  }

  const [removeMessage] = filterCometMessages<RemoveMessage>(
    socketResult as SocketData<RemoveMessage>[],
    createRemovedMessageChannel(userId)
  )

  const [removeIncomingMessage] = filterCometMessages<BaseRemoveMessage>(
    socketResult as SocketData<RemoveMessage>[],
    createRemovedIncomingMessageChannel(userId)
  )

  const [incomingInfo] = filterCometMessages<MessageFromCometContent>(
    socketResult as SocketData<MessageFromCometContent>[],
    createLastUserChannel(userId),
    {
      actions: [ActionComet.Replace, ActionComet.Publish],
      withContent: true,
    }
  )

  const incomingMessages = filterCometMessages<MessengerTextMessageComet>(
    socketResult as SocketData<MessengerTextMessageComet>[],
    createMessengerMessageChannel(userId),
    {
      actions: [ActionComet.Replace, ActionComet.Publish],
      withContent: true,
    }
  )

  /**
   * Переделал логику сокета, должно приходить только одно сообщение.
   */
  if (incomingMessages.length) {
    return incomingMessages.reduce((acc, message) => {
      return {
        ...acc,
        ...addIncomingMessage(message, chatState),
      }
    }, chatState)
  }

  const outgoingMessages = filterCometMessages<MessengerTextMessageComet>(
    socketResult as SocketData<MessengerTextMessageComet>[],
    createMessengerMessageOutChannel(userId),
    {
      actions: [ActionComet.Replace, ActionComet.Publish],
      withContent: true,
    }
  )

  if (outgoingMessages.length) {
    return outgoingMessages.reduce((acc, message) => {
      return {
        ...acc,
        ...addOutgoingMessage(message, chatState, chatSelectedProfileId),
      }
    }, chatState)
  }

  const [chatDataOutUpdate] = filterCometMessages<MessageFromCometContent>(
    socketResult as SocketData<MessageFromCometContent>[],
    createLastUserOutChannel(userId),
    {
      actions: [ActionComet.Replace, ActionComet.Publish],
      withContent: true,
    }
  )

  const [messageEdited] = filterCometMessages<MessageEditedCometContent>(
    socketResult as SocketData<MessageEditedCometContent>[],
    createMessageEditedChannel(userId)
  )

  const [readerMessages] = filterCometMessages(
    socketResult as SocketData<SocketContent>[],
    createMessageReadedChannel(userId)
  )

  const [typingMessages] = filterCometMessages(
    socketResult as SocketData<SocketContent>[],
    createTypingMessageUserChannel(userId)
  )

  const [supportTickerClosed] = filterCometMessages(
    socketResult as SocketData<SocketContent>[],
    createSupportTickerClosedChannel(userId)
  )

  const [ignoredMessage] = filterCometMessages(
    socketResult as SocketData<SocketContent>[],
    createMessengerIgnoredChannel(userId)
  )

  const [vipArray] = filterCometMessages(
    socketResult as SocketData<SocketContent>[],
    createVipStatusChannel(userId)
  )

  const [messengerAutodelete] = filterCometMessages<RemoveMessage>(
    socketResult as SocketData<RemoveMessage>[],
    createMessengerAutodeleteChannel(userId)
  )

  /** Были открыты/закрыты все фото контакта */
  const [attachmentVisibilityContactModified] =
    filterCometMessages<AttachmentVisibilityContactModified>(
      socketResult as SocketData<AttachmentVisibilityContactModified>[],
      createAttachmentVisibilityContactModifiedChannel(userId)
    )
  if (attachmentVisibilityContactModified) {
    return updateAttachmentVisibilityContactModified(
      attachmentVisibilityContactModified,
      chatState
    )
  }

  /** Были открыты/закрыты все фото сообщения */
  const [attachmentVisibilityMessageModified] =
    filterCometMessages<AttachmentVisibilityMessageModified>(
      socketResult as SocketData<AttachmentVisibilityMessageModified>[],
      createAttachmentVisibilityMessageModifiedChannel(userId)
    )
  if (attachmentVisibilityMessageModified) {
    return updateAttachmentVisibilityMessageModified(
      attachmentVisibilityMessageModified,
      chatState
    )
  }

  const [addedReaction] = filterCometMessages<AddReactionMessage>(
    socketResult as SocketData<AddReactionMessage>[],
    createMessageAddReactionChannel(userId)
  )

  if (addedReaction) {
    return updateMessagesWithAddedReaction(chatState, addedReaction.content)
  }

  const [replacedReaction] = filterCometMessages<ReplacedReactionMessage>(
    socketResult as SocketData<ReplacedReactionMessage>[],
    createMessageReplaceReactionChannel(userId)
  )

  if (replacedReaction) {
    return updateMessagesWithReplacedReaction(
      chatState,
      replacedReaction.content
    )
  }

  const [removedReaction] = filterCometMessages<RemovedReactionMessage>(
    socketResult as SocketData<RemovedReactionMessage>[],
    createMessageRemoveReactionChannel(userId)
  )

  if (removedReaction) {
    return updateMessagesWithRemovedReaction(chatState, removedReaction.content)
  }

  const [readReaction] = filterCometMessages<ReadReactionMessage>(
    socketResult as SocketData<ReadReactionMessage>[],
    createMessageReadReactionChannel(userId)
  )

  if (readReaction) {
    return updateMessagesWithReadReaction(chatState, readReaction.content)
  }

  if (
    messengerAutodelete &&
    messengerAutodelete.content.contactId === chatState.chatData.contactId &&
    messengerAutodelete.content.contactUserId === chatState.chatData.anketa!.id
  ) {
    return {
      ...chatState,
      closeChat: true,
    }
  }

  if (
    removeMessage &&
    removeMessage.channel === createRemovedMessageChannel(userId) &&
    removeMessage.content.removedMessagesIds.length > 0
  ) {
    return removeMessageFromChat(
      chatState,
      removeMessage.content,
      MessengerItemTypes.REMOVED
    )
  }

  if (
    removeIncomingMessage &&
    removeIncomingMessage.channel ===
      createRemovedIncomingMessageChannel(userId) &&
    removeIncomingMessage.content.removedMessagesIds.length > 0
  ) {
    return removeMessageFromChat(
      chatState,
      removeIncomingMessage.content,
      MessengerItemTypes.RemovedForRecipient
    )
  }

  if (incomingInfo) {
    return updateChatData(chatState, incomingInfo)
  }

  if (
    chatDataOutUpdate &&
    Number(chatDataOutUpdate.content.contact.lastMessage!.user_to_id) ===
      chatState.selectedProfileId
  ) {
    return updateChatDataFromOutgoing(chatDataOutUpdate, chatState)
  } else if (chatDataOutUpdate) {
    /** Смысл писать про ошибку, только если в принципе есть сообщение */
    console.warn(
      'Wrong chat update message',
      chatDataOutUpdate,
      'selected',
      chatState.selectedProfileId
    )
  }

  // mark unread message readed
  if (
    readerMessages?.content &&
    readerMessages.action === ActionComet.Publish
  ) {
    return markMessagesAsReaded(
      readerMessages as SocketData<{ anketaId: number }>,
      chatState
    )
  }

  // User typing message
  if (
    typingMessages?.content &&
    typingMessages.action === ActionComet.Replace
  ) {
    return addTypingSignal(
      typingMessages as SocketData<MessageTypingFromCometContent>,
      chatState
    )
  }

  // Closed ticket from support
  if (
    supportTickerClosed?.content &&
    supportTickerClosed.action === ActionComet.Publish &&
    chatState.isSupport
  ) {
    return updateChat
  }

  // Обновление чата после получение випа. чтобы убрать стоп чат.
  if (vipArray?.content && vipArray.action === ActionComet.Publish) {
    return updateChat
  }

  if (
    ignoredMessage?.content &&
    (ignoredMessage.action === ActionComet.Publish ||
      ignoredMessage.action === ActionComet.Remove)
  ) {
    // Ignored message
    return addIgnoredMessage(
      ignoredMessage as SocketData<MessageIgnoredFromCometContent>,
      chatState
    )
  }

  if (messageEdited?.content) {
    return updateEditedMessage(chatState, messageEdited)
  }

  const [requestStatusChangedSocketData] =
    filterCometMessages<RequestForCommunicationStatusChangedComet>(
      socketResult as SocketData<RequestForCommunicationStatusChangedComet>[],
      createMessengerRequestForCommunicationStatusChangedChannel(userId)
    )

  if (requestStatusChangedSocketData?.content) {
    const {
      content: { contactAnketaId, status },
    } = requestStatusChangedSocketData

    if (chatState.chatData.anketa?.id === contactAnketaId) {
      return {
        ...chatState,
        requestStatus: status,
      }
    }
  }

  const [liveness] = filterCometMessages<LivenessComet>(
    socketResult as SocketData<LivenessComet>[],
    createLivenessSessionResultChannel(userId)
  )

  if (liveness?.content?.sessionStatus) {
    return updateChat
  }

  const [chatUpdate] = filterCometMessages(
    socketResult as SocketData<ChatDataUpdate>[],
    createSendingPhotoAbility(chatState.chatData.anketa?.id!)
  )

  if (chatUpdate?.content) {
    return {
      ...chatState,
      chatData: {
        ...chatState.chatData,
        isPrivatePhotoEnabled: chatUpdate.content.userCanSendPrivatePhoto,
      },
    }
  }

  return chatState
}
